// GlobalTooltip upgrades every native `title` in the app to a fast, styled
// tooltip — mounted once at the app root, no per-button changes needed. On
// hover it moves the element's `title` into a private `data-tt` attribute (so
// the slow, unstyled OS tooltip never shows), then renders a themed bubble after
// a short delay. On leave it restores `title`. Also honors an explicit
// `data-tip` attribute for elements that opt out of a real `title`.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface TipState {
  x: number;
  y: number;
  text: string;
  below: boolean;
}

export function GlobalTooltip() {
  const [tip, setTip] = useState<TipState | null>(null);
  const cur = useRef<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const bubble = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Restore a stolen native title so a11y / re-hover keep working.
    const restore = (el: HTMLElement | null) => {
      if (!el) return;
      const stolen = el.getAttribute('data-tt');
      if (stolen !== null) {
        // Only put the stolen title back if React hasn't re-added a fresh one in
        // the meantime (a re-render that changed the title) — else we'd clobber
        // the current value with a stale one.
        if (!el.hasAttribute('title')) el.setAttribute('title', stolen);
        el.removeAttribute('data-tt');
      }
    };

    const clear = () => {
      if (timer.current) clearTimeout(timer.current);
      setTip(null);
    };

    const show = (el: HTMLElement, text: string) => {
      const r = el.getBoundingClientRect();
      // Prefer above; flip below when too close to the top edge.
      const below = r.top < 46;
      const x = Math.min(Math.max(r.left + r.width / 2, 8), window.innerWidth - 8);
      setTip({ x, y: below ? r.bottom : r.top, text, below });
    };

    const onOver = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[title],[data-tt],[data-tip]') || null;
      if (el === cur.current) return; // still on the same target
      restore(cur.current);
      clear();
      cur.current = el;
      if (!el) return;
      const text = el.getAttribute('title') ?? el.getAttribute('data-tt') ?? el.getAttribute('data-tip') ?? '';
      if (!text.trim()) { cur.current = null; return; }
      // Steal the native title so the OS tooltip never appears.
      if (el.hasAttribute('title')) {
        el.setAttribute('data-tt', el.getAttribute('title') as string);
        el.removeAttribute('title');
      }
      timer.current = setTimeout(() => { if (cur.current === el) show(el, text); }, 110);
    };

    const onOut = (e: MouseEvent) => {
      if (!cur.current) return;
      const to = e.relatedTarget as Node | null;
      if (to && cur.current.contains(to)) return; // moved within the same element
      restore(cur.current);
      cur.current = null;
      clear();
    };

    const onGone = () => { restore(cur.current); cur.current = null; clear(); };

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    // A tooltip pinned to fixed coords goes stale on scroll / click — just drop it.
    window.addEventListener('scroll', onGone, true);
    document.addEventListener('mousedown', onGone, true);
    // Watchdog: if the hovered element is removed/replaced by a background poll
    // re-render while the mouse is held still, no mouseout fires — drop the tip
    // so it doesn't linger stuck on screen. Cheap (only acts while hovering).
    const watchdog = setInterval(() => {
      if (cur.current && !cur.current.isConnected) onGone();
    }, 500);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      window.removeEventListener('scroll', onGone, true);
      document.removeEventListener('mousedown', onGone, true);
      clearInterval(watchdog);
      restore(cur.current);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  // Nudge the bubble back on screen once it has a size. It is laid out at
  // left:0 and moved with a transform (see --tt-x in styles.css) so that its
  // width is decided by max-width rather than by how much room is left to the
  // right of the target — positioning it with `left` made a bubble near the
  // right edge shrink to a sliver of three-word lines instead of moving.
  //
  // useLayoutEffect, so the shift lands before the browser paints and nothing
  // is seen to jump. The arrow moves the opposite way and keeps pointing at
  // the thing being described.
  useLayoutEffect(() => {
    const el = bubble.current;
    if (!el) return;
    el.style.setProperty('--tt-shift', '0px');
    const r = el.getBoundingClientRect();
    const margin = 8;
    let shift = 0;
    if (r.right > window.innerWidth - margin) shift = window.innerWidth - margin - r.right;
    else if (r.left < margin) shift = margin - r.left;
    // Keep the arrow inside the bubble, clear of its rounded corners: the arrow
    // is 8px wide over a 6px radius, so 8px in from the edge is the closest it
    // can sit and still look attached. Past that the bubble stays where it is
    // rather than pointing from nowhere — nothing in this app is hovered that
    // close to the screen edge anyway.
    const room = Math.max(0, r.width / 2 - 8);
    shift = Math.max(-room, Math.min(room, shift));
    el.style.setProperty('--tt-shift', `${Math.round(shift)}px`);
  }, [tip]);

  if (!tip) return null;
  return (
    <div
      ref={bubble}
      className={'tt-bubble' + (tip.below ? ' tt-below' : '')}
      style={{ top: tip.y, ['--tt-x' as string]: `${tip.x}px` } as React.CSSProperties}
      role="tooltip"
    >
      {tip.text}
    </div>
  );
}
